import { useTranslation } from '../../../i18n';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import html2canvas from 'html2canvas';
import './ShareTrainingCardPanel.css';

// ✅ Logo Tzone (ajusta si tu ruta real es otra)
import tzoneLogo from '../../../assets/icons/logo.png';

// ===== util: File -> dataURL (sin CORS) =====
function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result || ''));
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

// ===== coords / bounds =====
function computeBounds(points) {
  if (!Array.isArray(points) || points.length < 2) return null;
  let minLat = 999, maxLat = -999, minLon = 999, maxLon = -999;

  for (const p of points) {
    const lat = Array.isArray(p) ? Number(p[0]) : NaN;
    const lon = Array.isArray(p) ? Number(p[1]) : NaN;
    if (!Number.isFinite(lat) || !Number.isFinite(lon)) continue;
    minLat = Math.min(minLat, lat);
    maxLat = Math.max(maxLat, lat);
    minLon = Math.min(minLon, lon);
    maxLon = Math.max(maxLon, lon);
  }
  if (minLat === 999) return null;
  return { minLat, maxLat, minLon, maxLon };
}

// ===== WebMercator helpers =====
function lonLatToWorldPx(lon, lat, z) {
  const sinLat = Math.sin((lat * Math.PI) / 180);
  const scale = 256 * Math.pow(2, z);
  const x = ((lon + 180) / 360) * scale;
  const y = (0.5 - Math.log((1 + sinLat) / (1 - sinLat)) / (4 * Math.PI)) * scale;
  return { x, y };
}
function worldPxToTileXY(x, y) {
  return { tx: Math.floor(x / 256), ty: Math.floor(y / 256) };
}

function chooseZoomForBounds(bounds, W, H, padPx = 180) {
  if (!bounds) return 13;
  const { minLat, maxLat, minLon, maxLon } = bounds;
  const targetW = Math.max(1, W - padPx * 2);
  const targetH = Math.max(1, H - padPx * 2);

  let best = 13;
  for (let z = 6; z <= 17; z++) {
    const a = lonLatToWorldPx(minLon, maxLat, z);
    const b = lonLatToWorldPx(maxLon, minLat, z);
    const spanW = Math.abs(b.x - a.x);
    const spanH = Math.abs(b.y - a.y);
    if (spanW <= targetW && spanH <= targetH) best = z;
  }
  return best;
}

async function fetchTileImage(url) {
  const res = await fetch(url, { mode: 'cors' });
  if (!res.ok) throw new Error('tile fetch failed');
  const blob = await res.blob();
  const objUrl = URL.createObjectURL(blob);

  const img = await new Promise((resolve, reject) => {
    const i = new Image();
    i.crossOrigin = 'anonymous';
    i.onload = () => resolve(i);
    i.onerror = reject;
    i.src = objUrl;
  });

  URL.revokeObjectURL(objUrl);
  return img;
}

/**
 * ✅ Genera un fondo mapa (tipo Strava) como dataURL PNG:
 * - Construye tiles CARTO en canvas 1080x1920
 * - Aplica overlay oscuro inferior para legibilidad
 */
async function buildStaticMapDataUrl({
  points,
  W = 1080,
  H = 1920,
  tileTemplate = 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
  subdomains = ['a', 'b', 'c'],
}) {
  const bounds = computeBounds(points);
  if (!bounds) return '';

  const cLat = (bounds.minLat + bounds.maxLat) / 2;
  const cLon = (bounds.minLon + bounds.maxLon) / 2;

  const z = chooseZoomForBounds(bounds, W, H, 200);

  const center = lonLatToWorldPx(cLon, cLat, z);
  const topLeftWorld = { x: center.x - W / 2, y: center.y - H / 2 };

  const tlTile = worldPxToTileXY(topLeftWorld.x, topLeftWorld.y);
  const brTile = worldPxToTileXY(topLeftWorld.x + W, topLeftWorld.y + H);

  const canvas = document.createElement('canvas');
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext('2d');

  // fondo suave si algún tile falla
  ctx.fillStyle = '#eef2f7';
  ctx.fillRect(0, 0, W, H);

  const jobs = [];
  for (let ty = tlTile.ty; ty <= brTile.ty; ty++) {
    for (let tx = tlTile.tx; tx <= brTile.tx; tx++) {
      const s = subdomains[Math.abs(tx + ty) % subdomains.length];
      const url = tileTemplate
        .replace('{s}', s)
        .replace('{z}', String(z))
        .replace('{x}', String(tx))
        .replace('{y}', String(ty))
        .replace('{r}', '');

      jobs.push((async () => {
        try {
          const img = await fetchTileImage(url);
          const tileWorldX = tx * 256;
          const tileWorldY = ty * 256;
          const dx = Math.round(tileWorldX - topLeftWorld.x);
          const dy = Math.round(tileWorldY - topLeftWorld.y);
          ctx.drawImage(img, dx, dy, 256, 256);
        } catch {
          // no rompe
        }
      })());
    }
  }

  await Promise.all(jobs);

  // overlay oscuro inferior (como Strava)
  const grad = ctx.createLinearGradient(0, H * 0.35, 0, H);
  grad.addColorStop(0, 'rgba(0,0,0,0.00)');
  grad.addColorStop(1, 'rgba(0,0,0,0.62)');
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, W, H);

  return canvas.toDataURL('image/png');
}

// ===== overlay ruta (SVG) =====
function RouteSvgOverlay({ points, W = 1080, H = 1920, pad = 170 }) {
  const path = useMemo(() => {
    const pts = Array.isArray(points) ? points : [];
    if (pts.length < 2) return { d: '', start: null, end: null };

    let minLat = Infinity, maxLat = -Infinity, minLon = Infinity, maxLon = -Infinity;
    for (const [lat, lon] of pts) {
      if (!Number.isFinite(lat) || !Number.isFinite(lon)) continue;
      minLat = Math.min(minLat, lat);
      maxLat = Math.max(maxLat, lat);
      minLon = Math.min(minLon, lon);
      maxLon = Math.max(maxLon, lon);
    }
    if (!Number.isFinite(minLat)) return { d: '', start: null, end: null };

    const dx = Math.max(1e-9, maxLon - minLon);
    const dy = Math.max(1e-9, maxLat - minLat);

    const scaleX = (W - pad * 2) / dx;
    const scaleY = (H - pad * 2) / dy;
    const s = Math.min(scaleX, scaleY);

    const ox = pad + (W - pad * 2 - dx * s) / 2;
    const oy = pad + (H - pad * 2 - dy * s) / 2;

    const toXY = ([lat, lon]) => {
      const x = ox + (lon - minLon) * s;
      const y = oy + (maxLat - lat) * s;
      return [x, y];
    };

    const xy = pts.map(toXY);

    const d = xy
      .map(([x, y], i) => `${i === 0 ? 'M' : 'L'} ${x.toFixed(1)} ${y.toFixed(1)}`)
      .join(' ');

    return { d, start: xy[0], end: xy[xy.length - 1] };
  }, [points, W, H, pad]);

  return (
    <svg className="tzone-rrss-routeOverlay" viewBox={`0 0 ${W} ${H}`} width={W} height={H} aria-hidden="true">
      {path.d ? (
        <>
          {/* glow */}
          <path
            d={path.d}
            fill="none"
            stroke="rgba(255,255,255,0.20)"
            strokeWidth="12"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          {/* main */}
          <path
            d={path.d}
            fill="none"
            stroke="#ff7a18"
            strokeWidth="7"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          {path.start && <circle cx={path.start[0]} cy={path.start[1]} r="10" fill="#22c55e" />}
          {path.end && <circle cx={path.end[0]} cy={path.end[1]} r="10" fill="#ef4444" />}
        </>
      ) : null}
    </svg>
  );
}

// ===== formatos =====
function fmtKm(meters) {
  const km = (Number(meters || 0) / 1000);
  return km > 0 ? km.toFixed(2) : '0.00';
}
function fmtDuration(sec) {
  const s = Math.max(0, Math.floor(Number(sec || 0)));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const ss = s % 60;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${ss}s`;
  return `${ss}s`;
}
function fmtPace(secPerKm) {
  const p = Number(secPerKm);
  if (!Number.isFinite(p) || p <= 0) return '--';
  const m = Math.floor(p / 60);
  const s = Math.floor(p % 60);
  return `${m}:${String(s).padStart(2, '0')} /km`;
}

// ✅ NUEVO: helpers para ciclismo (sin romper nada)
function normalizeType(t) {
  const s = String(t || '').toLowerCase();
  if (s.includes('cicl')) return 'ciclismo';
  if (s.includes('bike') || s.includes('cycling')) return 'ciclismo';
  if (s.includes('run') || s.includes('correr')) return 'correr';
  if (s.includes('swim') || s.includes('nat')) return 'swim';
  if (s.includes('strength') || s.includes('fuerza') || s.includes('gym')) return 'strength';
  return s || 'otro';
}
function fmtSpeedKmh(kmh) {
  const v = Number(kmh);
  if (!Number.isFinite(v) || v <= 0) return '--';
  return `${v.toFixed(2)} km/h`;
}

export default function ShareTrainingCardPanel({
  activity,
  photoUrl = null,     // opcional: si en el futuro quieres pasarla desde afuera
  mapImageUrl = null,  // opcional: si en el futuro quieres pasar un mapa ya generado
  onClose,
}) {
  const { t } = useTranslation();
  const W = 1080;
  const H = 1920;

  const wrapRef = useRef(null);
//  const exportRef = useRef(null);

  // modos: auto = foto si existe, si no mapa; map = mapa; photo = foto
  const [bgMode, setBgMode] = useState('auto'); // 'auto' | 'map' | 'photo'
  const [bgPhotoDataUrl, setBgPhotoDataUrl] = useState('');
  const [mapBgDataUrl, setMapBgDataUrl] = useState('');
  const [buildingMap, setBuildingMap] = useState(false);

  const routePoints = useMemo(() => {
    const pts = activity?.routePoints;
    return Array.isArray(pts) ? pts : [];
  }, [activity?.routePoints]);

  const completed = activity?.completed || {};
  const name = activity?.name || 'Entrenamiento';

  const distanceM = completed?.distance || 0;
  const durationSec = completed?.durationSec || 0;
  const paceSec = completed?.pace ?? null;

  // ✅ NUEVO: tipo y velocidad promedio robusta (misma fuente: distance/duration)
  const typeNorm = useMemo(() => {
    return normalizeType(activity?.type || activity?.sport || activity?.activityType || activity?.name);
  }, [activity?.type, activity?.sport, activity?.activityType, activity?.name]);

  const avgSpeedKmh = useMemo(() => {
    // 1) viene explícito en km/h
    if (Number.isFinite(Number(completed?.avgSpeedKmh))) {
      return Number(completed.avgSpeedKmh);
    }
    // 2) viene en m/s
    if (Number.isFinite(Number(completed?.avgSpeed))) {
      return Number(completed.avgSpeed) * 3.6;
    }
    // 3) fallback seguro: distancia/tiempo (km/h)
    const distKm = Number(completed?.distance) / 1000;
    const hours = Number(completed?.durationSec) / 3600;
    if (distKm > 0 && hours > 0) return distKm / hours;
    return null;
  }, [completed?.avgSpeedKmh, completed?.avgSpeed, completed?.distance, completed?.durationSec]);

  const thirdMetric = useMemo(() => {
    if (typeNorm === 'ciclismo') {
      return { label: 'Vel. Promedio', value: fmtSpeedKmh(avgSpeedKmh) };
    }
    // mantenemos lo actual para el resto: Ritmo
    return { label: 'Ritmo', value: fmtPace(paceSec) };
  }, [typeNorm, avgSpeedKmh, paceSec]);

  // sync props -> state (sin romper tu integración actual: tú pasas null)
  useEffect(() => {
    if (photoUrl && typeof photoUrl === 'string') setBgPhotoDataUrl(photoUrl);
  }, [photoUrl]);

  useEffect(() => {
    if (mapImageUrl && typeof mapImageUrl === 'string') setMapBgDataUrl(mapImageUrl);
  }, [mapImageUrl]);

  // ✅ construir mapa si corresponde (solo si NO hay foto, o si forzamos map)
  useEffect(() => {
    let mounted = true;

    const hasRoute = Array.isArray(routePoints) && routePoints.length > 1;

    const wantMap =
      hasRoute &&
      (bgMode === 'map' || (bgMode === 'auto' && !bgPhotoDataUrl)) &&
      !mapImageUrl; // si viene por props, no construimos

    if (!wantMap) return;

    (async () => {
      try {
        setBuildingMap(true);
        const dataUrl = await buildStaticMapDataUrl({ points: routePoints, W, H });
        if (mounted) setMapBgDataUrl(dataUrl || '');
      } catch {
        if (mounted) setMapBgDataUrl('');
      } finally {
        if (mounted) setBuildingMap(false);
      }
    })();

    return () => { mounted = false; };
  }, [bgMode, bgPhotoDataUrl, routePoints, W, H, mapImageUrl]);

  const backgroundStyle = useMemo(() => {
    const hasPhoto = !!bgPhotoDataUrl;
    const hasMap = !!mapBgDataUrl;

    if (bgMode === 'photo' && hasPhoto) {
      return {
        backgroundImage: `url(${bgPhotoDataUrl})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      };
    }

    if (
      (bgMode === 'map' || (bgMode === 'auto' && !hasPhoto)) &&
      hasMap
    ) {
      return {
        backgroundImage: `url(${mapBgDataUrl})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      };
    }

    // fallback elegante
    return {
      background: 'radial-gradient(1200px 900px at 30% 25%, #bfc6cf 0%, #30343a 55%, #0b0d12 100%)',
    };
  }, [bgMode, bgPhotoDataUrl, mapBgDataUrl]);

  const handlePickBg = useCallback(async (e) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    const dataUrl = await fileToDataUrl(f);
    setBgPhotoDataUrl(dataUrl);
    setBgMode('photo');
  }, []);

  const captureToBlob = useCallback(async () => {
    const node = wrapRef.current;
    if (!node) throw new Error('No hay card para capturar');

    // 1) tamaño real renderizado (el que realmente ve html2canvas)
    const rect = node.getBoundingClientRect();

    // 2) escala exacta para que el output sea 1080x1920
    const scaleX = W / rect.width;
    const scaleY = H / rect.height;
    const scale = Math.min(scaleX, scaleY);

    const canvas = await html2canvas(node, {
      backgroundColor: '#000000',   // evita transparencia “blanca” en visores
      scale,
      useCORS: true,
      allowTaint: false,
      logging: false,
      imageTimeout: 15000,
      scrollX: 0,
      scrollY: 0,
    });

    // 3) normaliza SIEMPRE a 1080x1920 exacto (cero marcos)
    const out = document.createElement('canvas');
    out.width = W;
    out.height = H;
    const ctx = out.getContext('2d');

    // centra por si html2canvas devuelve 1-2px diferente
    const dx = Math.round((W - canvas.width) / 2);
    const dy = Math.round((H - canvas.height) / 2);

    ctx.fillStyle = '#000000';
    ctx.fillRect(0, 0, W, H);
    ctx.drawImage(canvas, dx, dy);

    const blob = await new Promise((resolve) => out.toBlob(resolve, 'image/png', 1));
    if (!blob) throw new Error('No se pudo generar la imagen');
    return blob;
  }, [W, H]);

  const downloadPng = useCallback(async () => {
    const blob = await captureToBlob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `tzone-rrss-${Date.now()}.png`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }, [captureToBlob]);

  const shareNative = useCallback(async () => {
    const blob = await captureToBlob();
    const file = new File([blob], `tzone-rrss-${Date.now()}.png`, { type: 'image/png' });

    const canShareFiles =
      !!navigator.share && (!navigator.canShare || navigator.canShare({ files: [file] }));

    if (canShareFiles) {
      await navigator.share({
        title: 'Mi entrenamiento en Tzone',
        text: 'Compartido desde Tzone',
        files: [file],
      });
      return;
    }

    await downloadPng();
    alert(t('social.noSoportaCompartir'));
  }, [captureToBlob, downloadPng, t]);

  const hasRoute = Array.isArray(routePoints) && routePoints.length > 1;



  return (
    <div className="tzone-rrss-panel">
      <div className="tzone-rrss-actions">
        <button type="button" className="tzone-rrss-btn" onClick={shareNative}>{t('social.share')}</button>
        <button type="button" className="tzone-rrss-btn" onClick={downloadPng}>{t('social.sharePng')}</button>
        <button type="button" className="tzone-rrss-btn ghost" onClick={onClose}>{t('social.close')}</button>

        <div style={{ flex: 1 }} />

        {/* ✅ Controles de fondo (sin romper nada) */}
        <label className="tzone-rrss-btn ghost" style={{ cursor: 'pointer' }}>
          Foto fondo
          <input type="file" accept="image/*" onChange={handlePickBg} style={{ display: 'none' }} />
        </label>

        <button
          type="button"
          className={`tzone-rrss-btn ghost ${bgMode === 'auto' ? 'is-active' : ''}`}
          onClick={() => setBgMode('auto')}
          title={t("shareTrainingCard.autoPhoto")}
        >
          Auto
        </button>

        <button
          type="button"
          className={`tzone-rrss-btn ghost ${bgMode === 'map' ? 'is-active' : ''}`}
          onClick={() => setBgMode('map')}
          disabled={!hasRoute}
          title={!hasRoute ? 'No hay ruta para mapa' : 'Mapa como fondo'}
        >
          Mapa
        </button>

        <button
          type="button"
          className={`tzone-rrss-btn ghost ${bgMode === 'photo' ? 'is-active' : ''}`}
          onClick={() => setBgMode('photo')}
          disabled={!bgPhotoDataUrl}
          title={!bgPhotoDataUrl ? 'Primero carga una foto' : 'Foto como fondo'}
        >
          Foto
        </button>
      </div>

      <div className="tzone-rrss-stage">
        <div ref={wrapRef} className="tzone-rrss-card" style={backgroundStyle}>
          {/* ✅ gradiente para legibilidad */}
          <div className="tzone-rrss-topGradient" />

          {/* logo */}
          <div className="tzone-rrss-top">
            <img className="tzone-rrss-logo" src={tzoneLogo} alt="Tzone" />
          </div>

          {/* overlay ruta (siempre arriba, incluso con foto) */}
          {hasRoute ? <RouteSvgOverlay points={routePoints} W={W} H={H} pad={170} /> : null}

          {/* bloque inferior */}
          <div className="tzone-rrss-bottom">
            <div className="tzone-rrss-title">{name}</div>

            <div className="tzone-rrss-grid">
              <div className="tzone-rrss-metric">
                <div className="k">{t('summary.distance')}</div>
                <div className="v">{fmtKm(distanceM)} km</div>
              </div>

              <div className="tzone-rrss-metric">
                <div className="k">{t("social.duracion")}</div>
                <div className="v">{fmtDuration(durationSec)}</div>
              </div>

              {/* ✅ CAMBIO: 3er KPI dinámico también aquí */}
              <div className="tzone-rrss-metric">
                <div className="k">{thirdMetric.label}</div>
                <div className="v">{thirdMetric.value}</div>
              </div>
            </div>

            {(buildingMap && (bgMode === 'map' || (bgMode === 'auto' && !bgPhotoDataUrl))) ? (
              <div className="tzone-rrss-hint">{t("shareTrainingCard.generatingMap")}</div>
            ) : (
              <div className="tzone-rrss-hint">tzone.run</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
